import React, { useContext, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from '@gatsbyjs/reach-router';
import { WebsiteLink } from 'src/components/common/website-link';
import {
  flex,
  shadow,
  STACKED_STICKY_ITEM_HEIGHT_REDUCTION,
  zIndexes,
} from 'src/styling/constants';
import { SiteContext } from 'src/context/site-context';
import type { Styling } from 'src/context/styling';
import type { CSSInterpolation } from '@emotion/serialize';
import { MENU_HIDE_DURATION_MS } from 'src/components/layout/redesign/header-desktop/header-menu';
import { getBackgroundColorByTheme } from 'src/helpers/styling';
import { BackgroundTheme } from 'src/enums/background-theme';
import { amplitudeTrackEvent } from 'src/helpers/amplitude-event-track';
import { REDESIGN_BREADCRUMBS_HEIGHT_CSS_VARIABLE } from 'src/components/common/breadcrumbs';
import { isOnClubsPage } from 'src/helpers/path-matching-helper';
import { HeaderActions } from './header-action';
import { HeaderNavigation } from 'src/components/layout/redesign/header-desktop/header-navigation';
import useHeldState from 'src/custom-hooks/use-held-state';
import type { HeaderButtonProps } from 'src/components/layout/redesign/header-mobile/header-button';
import type { BaseHeaderContent } from 'src/components/layout/redesign/header-mobile/header-menu';
import {
  HEADER_ADDITIONAL_TOP_PADDING_PX,
  mqDesktopHeader,
  mqHeaderDisplay,
  REDESIGN_HEADER_HEIGHT_CSS_VARIABLE,
} from 'src/components/layout/redesign/header-shared/header-styling';
import { HeaderLogo } from 'src/components/layout/redesign/header-shared/header-logo';
import { BuildType } from 'src/enums/build/build-type';

const createComponentStyling = (styling: Styling, buildType: BuildType, isClubsPage: boolean) =>
  ({
    container: [
      mqHeaderDisplay({
        'backgroundColor': getBackgroundColorByTheme(BackgroundTheme.LIGHT),
        'width': '100%',
        'display': ['none', 'flex'],
        'flexDirection': 'column',
        'alignItems': 'center',
        'position': 'sticky',
        'top': `-${HEADER_ADDITIONAL_TOP_PADDING_PX}px`,
        'paddingTop': `${HEADER_ADDITIONAL_TOP_PADDING_PX}px`,
        'marginTop': `-${HEADER_ADDITIONAL_TOP_PADDING_PX}px`,
        'zIndex': zIndexes.header,
        '& ul': {
          margin: 0,
          padding: 0,
        },
      }),
      isClubsPage ? shadow.below : null,
    ],
    header: mqDesktopHeader({
      maxWidth: '92.25rem',
      width: '100%',
      padding: ['1rem 1.25rem', '1rem 1.25rem', '1rem 3.75rem'],
      color: styling.colors.capeCod,
    }),
    logo: {
      alignSelf: 'center',
      marginRight: buildType === BuildType.MERIDIAN ? 0 : '1.5rem',
    },
    endActionsWrapper: mqDesktopHeader({
      display: ['flex', 'none'],
      marginLeft: '0.75rem',
    }),
  }) satisfies Record<string, CSSInterpolation>;

type HeaderDesktopProps = {
  headerContent: BaseHeaderContent | null;
  ribbonChildren?: React.ReactNode;
  primaryButton?: HeaderButtonProps;
  secondaryButton?: HeaderButtonProps;
  onMenuChanged?: () => void;
  additionalSubMenus?: React.ReactNode;
};

const HeaderDesktop = ({
  headerContent,
  ribbonChildren,
  primaryButton,
  secondaryButton,
  onMenuChanged,
  additionalSubMenus,
}: HeaderDesktopProps) => {
  const { styling, pageLevel, isGlobalPage, buildType } = useContext(SiteContext);
  const location = useLocation();
  const isClubsPage = isOnClubsPage(location);
  const [displayedSubMenuIndex, setDisplayedSubMenuIndex, holdSubMenuIndexChangesUntilAfterMs] =
    useHeldState<number | null>(null);
  const [isUserTabbing, setIsUserTabbing] = useState<boolean>(false);
  const [canOpenSubMenu, setCanOpenSubMenu] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const componentStyling = createComponentStyling(styling, buildType, isClubsPage);

  const onFinishMenuAction = () => {
    onMenuChanged?.();
    closeAllSubMenus();
  };

  const closeAllSubMenus = () => {
    setDisplayedSubMenuIndex(null);
    holdSubMenuIndexChangesUntilAfterMs(MENU_HIDE_DURATION_MS); // Wait for the menu to fully close before re-enabling user interaction. Might also help UX when reduced motion is enabled.
  };

  const openSubMenu = (index: number) => {
    onMenuChanged?.();
    setDisplayedSubMenuIndex(index);
  };

  useLayoutEffect(() => {
    if (!containerRef.current) {
      return;
    }

    document.documentElement.style.setProperty(
      'scroll-padding-top',
      `calc(var(--${REDESIGN_HEADER_HEIGHT_CSS_VARIABLE}) + var(--${REDESIGN_BREADCRUMBS_HEIGHT_CSS_VARIABLE}, 0px))`
    );

    const updateHeaderHeightVariable = () => {
      const doc = document.documentElement;
      const containerHeight = containerRef.current?.getBoundingClientRect().height;

      if (!containerHeight) {
        return;
      }
      doc.style.setProperty(
        `--${REDESIGN_HEADER_HEIGHT_CSS_VARIABLE}`,
        `${containerHeight - HEADER_ADDITIONAL_TOP_PADDING_PX - STACKED_STICKY_ITEM_HEIGHT_REDUCTION}px`
      );
    };

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateHeaderHeightVariable);
    });

    resizeObserver.observe(containerRef.current);
    updateHeaderHeightVariable();

    return () => {
      document.documentElement.style.removeProperty('scroll-padding-top');
      resizeObserver.disconnect();
    };
  }, []);

  if (!headerContent) {
    return null;
  }

  const logoAmplitudeAction = () => {
    amplitudeTrackEvent('Click to Logo', {
      custom_page: window.location.origin,
      custom_level: pageLevel,
    });
  };

  const logoClick = () => {
    onFinishMenuAction();
    document.body.scrollIntoView({ behavior: 'smooth' });
    logoAmplitudeAction();
  };

  return (
    <header
      ref={containerRef}
      css={componentStyling.container}
      onPointerLeave={closeAllSubMenus}
      onPointerOver={() => setCanOpenSubMenu(true)}
    >
      <div onPointerOver={closeAllSubMenus} css={{ width: '100%' }}>
        {ribbonChildren}
      </div>
      <div css={[componentStyling.header, flex.row]}>
        {isGlobalPage && (
          <div
            css={[
              componentStyling.logo,
              mqDesktopHeader({
                display: ['none', 'none', 'initial'],
              }),
            ]}
            onPointerOver={closeAllSubMenus}
          >
            <WebsiteLink to="/" goToGlobal handleClick={logoClick}>
              <HeaderLogo davidLloydSize="12.5rem" meridianSize="12.75rem" />
            </WebsiteLink>
          </div>
        )}
        <HeaderNavigation
          onFinishMenuAction={onFinishMenuAction}
          onHover={closeAllSubMenus}
          logoClick={logoClick}
          isUserTabbing={isUserTabbing}
          headerContent={headerContent}
          primaryButton={primaryButton}
          secondaryButton={secondaryButton}
          openSubMenu={openSubMenu}
          displayedSubMenuIndex={displayedSubMenuIndex}
          setIsUserTabbing={setIsUserTabbing}
          setCanOpenSubMenu={setCanOpenSubMenu}
          canOpenSubMenu={canOpenSubMenu}
        />
        {isGlobalPage && (
          <div css={componentStyling.endActionsWrapper}>
            <HeaderActions
              primaryButton={primaryButton}
              secondaryButton={secondaryButton}
              onHover={closeAllSubMenus}
              linkDropdownCta={headerContent.linkDropdownCta}
            />
          </div>
        )}
        {additionalSubMenus}
      </div>
    </header>
  );
};

export default HeaderDesktop;
